import {
  INFRA_BILLING_KINDS,
  INFRA_CADENCES,
  INFRA_CATEGORIES,
  INFRA_CURRENCIES,
  InfraServiceInputSchema,
  InfraServicePatchSchema,
  refineInfraServiceMerged,
  type InfraBillingKind,
  type InfraCadence,
  type InfraCategory,
  type InfraCurrency,
  type InfraService,
  type InfraServiceInput,
  type InfraServicePatch,
} from '@carsfinders/contracts';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { t } from '../../../lib/i18n';
import { financeErrorMessage } from '../../finance/api';
import { useCreateInfraService, usePatchInfraService } from '../api';
import { useModal } from '../use-modal';

export interface InfraServiceFormState {
  name: string;
  provider: string;
  category: InfraCategory;
  purpose: string;
  billingUrl: string;
  notes: string;
  billingKind: InfraBillingKind;
  cadence: InfraCadence | '';
  amount: string;
  currency: InfraCurrency;
  amountIsEstimate: boolean;
  nextDueDate: string;
  remindDaysBefore: string;
}

type FieldName = keyof InfraServiceFormState;
export type FieldErrors = Partial<Record<FieldName, string>>;

export function initialFormState(service?: InfraService): InfraServiceFormState {
  return {
    name: service?.name ?? '',
    provider: service?.provider ?? '',
    category: service?.category ?? 'hosting',
    purpose: service?.purpose ?? '',
    billingUrl: service?.billingUrl ?? '',
    notes: service?.notes ?? '',
    billingKind: service?.billingKind ?? 'recurring',
    cadence: service?.cadence ?? (service ? '' : 'monthly'),
    amount: service ? String(service.amount) : '',
    currency: service?.currency ?? 'USD',
    amountIsEstimate: service?.amountIsEstimate ?? false,
    nextDueDate: service?.nextDueDate ?? '',
    remindDaysBefore: String(service?.remindDaysBefore ?? 3),
  };
}

const optional = (value: string): string | null => value.trim() || null;
/** `12,5` and `12.5` both parse; an empty field is NaN so the schema reports it. */
const toNumber = (value: string): number => (value.trim() === '' ? Number.NaN : Number(value.replace(',', '.')));

function issueKey(field: string, code: string): string {
  if (code === 'too_big' && (field === 'amount' || field === 'remindDaysBefore')) return 'range';
  if (code === 'too_big') return 'tooLong';
  if (field === 'billingUrl') return 'invalidUrl';
  if (field === 'nextDueDate') return code === 'custom' ? 'required' : 'invalidDate';
  if (field === 'amount' || field === 'remindDaysBefore') return code === 'too_small' ? 'range' : 'invalidNumber';
  return 'required';
}

/** The wire shape of the form, before validation. */
function candidateOf(form: InfraServiceFormState, active: boolean) {
  return {
    name: form.name,
    provider: optional(form.provider),
    category: form.category,
    purpose: optional(form.purpose),
    billingUrl: optional(form.billingUrl),
    notes: optional(form.notes),
    billingKind: form.billingKind,
    cadence: form.billingKind === 'one_off' ? null : form.cadence || null,
    amount: toNumber(form.amount),
    currency: form.currency,
    amountIsEstimate: form.amountIsEstimate,
    nextDueDate: optional(form.nextDueDate),
    remindDaysBefore: toNumber(form.remindDaysBefore),
    active,
  };
}

type Issue = { path: ReadonlyArray<string | number>; code: string };

function toFieldErrors(issues: readonly Issue[]): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of issues) {
    const field = String(issue.path[0] ?? 'name') as FieldName;
    if (!errors[field]) errors[field] = t(`admin.infraCosts.form.errors.${issueKey(field, issue.code)}`);
  }
  return errors;
}

const SCHEDULE_FIELDS = ['billingKind', 'cadence', 'nextDueDate'] as const;

export type ServiceFormResult =
  | { ok: true; mode: 'create'; value: InfraServiceInput }
  | { ok: true; mode: 'patch'; value: InfraServicePatch }
  | { ok: false; errors: FieldErrors };

/**
 * The form, validated by the contract schemas the api applies, so the client
 * cannot pass what the server refuses. Errors land under their fields, in the
 * catalogue's words.
 *
 * Creating: `InfraServiceInputSchema` (a recurring service needs a cadence and
 * a date). Editing: only the CHANGED fields are sent, checked by
 * `InfraServicePatchSchema`, and the recurring rule is re-checked on the merged
 * result only when a schedule field changed — exactly the server's rule, so a
 * seeded service without a date stays editable (its amount, its notes).
 */
export function validateServiceForm(form: InfraServiceFormState, service?: InfraService): ServiceFormResult {
  const candidate = candidateOf(form, service?.active ?? true);
  if (!service) {
    const result = InfraServiceInputSchema.safeParse(candidate);
    if (result.success) return { ok: true, mode: 'create', value: result.data };
    // zod skips the object-level recurring rule while a field is invalid; report
    // it together with the field errors so the operator sees everything at once.
    const recurring = refineInfraServiceMerged(candidate).map((i) => ({ ...i, code: 'custom' }));
    return { ok: false, errors: toFieldErrors([...result.error.issues, ...recurring]) };
  }

  const before = candidateOf(initialFormState(service), service.active);
  const changed = (Object.keys(candidate) as (keyof typeof candidate)[]).filter(
    (key) => !Object.is(candidate[key], before[key]),
  );
  const partial = Object.fromEntries(changed.map((key) => [key, candidate[key]]));
  const result = InfraServicePatchSchema.safeParse(partial);
  if (!result.success) return { ok: false, errors: toFieldErrors(result.error.issues) };

  if (changed.some((key) => (SCHEDULE_FIELDS as readonly string[]).includes(key))) {
    const merged = refineInfraServiceMerged({ ...service, ...result.data });
    if (merged.length > 0) return { ok: false, errors: toFieldErrors(merged.map((i) => ({ ...i, code: 'custom' }))) };
  }
  return { ok: true, mode: 'patch', value: result.data };
}

const INPUT =
  'h-11 w-full rounded-cf-md border bg-cf-surface px-3 text-[14px] text-cf-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-cf-ink aria-[invalid=true]:border-cf-danger';

/**
 * Add / edit drawer for one service (spec §6). Modal: focus is trapped inside,
 * Escape and the backdrop close it, focus returns to the trigger.
 */
export function InfraServiceDrawer({ service, onClose }: { service?: InfraService; onClose: () => void }) {
  const [form, setForm] = useState<InfraServiceFormState>(() => initialFormState(service));
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useCreateInfraService();
  const patch = usePatchInfraService();
  const busy = create.isPending || patch.isPending;
  const failure = create.error ?? patch.error;
  const { ref, onKeyDown } = useModal(onClose, busy);
  const titleId = useId();
  const baseId = useId();

  const set = <K extends FieldName>(field: K, value: InfraServiceFormState[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const result = validateServiceForm(form, service);
    if (!result.ok) {
      setErrors(result.errors);
      // Move focus to the first invalid field so the keyboard user lands on it.
      const first = Object.keys(result.errors)[0];
      if (first) ref.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
      return;
    }
    setErrors({});
    const options = { onSuccess: () => onClose() };
    if (result.mode === 'patch') {
      if (!service || Object.keys(result.value).length === 0) return onClose();
      patch.mutate({ id: service.id, patch: result.value }, options);
    } else {
      create.mutate(result.value, options);
    }
  };

  const field = (name: FieldName, label: string, control: (props: FieldProps) => ReactNode) => (
    <Field id={`${baseId}-${name}`} label={label} error={errors[name]}>
      {(props) => control({ ...props, name })}
    </Field>
  );

  const title = service ? t('admin.infraCosts.form.editTitle') : t('admin.infraCosts.form.createTitle');

  return (
    <>
      <div onClick={busy ? undefined : onClose} className="fixed inset-0 z-[60] bg-cf-ink/35 backdrop-blur-[2px]" aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-testid="infra-service-drawer"
        className="fixed inset-y-0 right-0 z-[61] flex w-[min(560px,100vw)] flex-col border-l border-cf-line bg-cf-ground shadow-[-24px_0_60px_rgba(0,0,0,0.16)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-cf-line-soft bg-cf-surface px-6 pt-5 pb-4 shadow-[inset_0_3px_0_var(--color-cf-accent)]">
          <h2 id={titleId} className="text-[22px] font-semibold tracking-[-0.02em]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t('admin.infraCosts.form.close')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cf-fill text-[18px] focus-visible:outline-2 focus-visible:outline-cf-ink"
          >
            ×
          </button>
        </div>

        <form noValidate onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-3.5 overflow-auto px-6 py-5">
            {field('name', t('admin.infraCosts.form.name'), (p) => (
              <input {...p} data-autofocus className={INPUT} value={form.name} onChange={(e) => set('name', e.target.value)} />
            ))}
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {field('provider', t('admin.infraCosts.form.provider'), (p) => (
                <input {...p} className={INPUT} value={form.provider} onChange={(e) => set('provider', e.target.value)} />
              ))}
              {field('category', t('admin.infraCosts.form.category'), (p) => (
                <select
                  {...p}
                  className={INPUT}
                  value={form.category}
                  onChange={(e) => set('category', e.target.value as InfraCategory)}
                >
                  {INFRA_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`admin.infraCosts.category.${c}`)}
                    </option>
                  ))}
                </select>
              ))}
            </div>
            {field('purpose', t('admin.infraCosts.form.purpose'), (p) => (
              <input {...p} className={INPUT} value={form.purpose} onChange={(e) => set('purpose', e.target.value)} />
            ))}
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {field('billingKind', t('admin.infraCosts.form.billingKind'), (p) => (
                <select
                  {...p}
                  className={INPUT}
                  value={form.billingKind}
                  onChange={(e) => set('billingKind', e.target.value as InfraBillingKind)}
                >
                  {INFRA_BILLING_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`admin.infraCosts.billingKind.${k}`)}
                    </option>
                  ))}
                </select>
              ))}
              {/* A one-off payment has no period: the field is hidden, and sent as null. */}
              {form.billingKind !== 'one_off' &&
                field('cadence', t('admin.infraCosts.form.cadence'), (p) => (
                  <select
                    {...p}
                    className={INPUT}
                    value={form.cadence}
                    onChange={(e) => set('cadence', e.target.value as InfraCadence | '')}
                  >
                    <option value="">{t('admin.infraCosts.form.cadenceNone')}</option>
                    {INFRA_CADENCES.map((c) => (
                      <option key={c} value={c}>
                        {t(`admin.infraCosts.cadence.${c}`)}
                      </option>
                    ))}
                  </select>
                ))}
            </div>
            <div className="grid grid-cols-[1fr_120px] gap-3.5">
              {field('amount', t('admin.infraCosts.form.amount'), (p) => (
                <input
                  {...p}
                  inputMode="decimal"
                  className={`${INPUT} font-mono`}
                  value={form.amount}
                  onChange={(e) => set('amount', e.target.value)}
                />
              ))}
              {field('currency', t('admin.infraCosts.form.currency'), (p) => (
                <select
                  {...p}
                  className={INPUT}
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value as InfraCurrency)}
                >
                  {INFRA_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 text-[13px] text-cf-ink">
              <input
                type="checkbox"
                name="amountIsEstimate"
                checked={form.amountIsEstimate}
                onChange={(e) => set('amountIsEstimate', e.target.checked)}
                className="h-4 w-4 accent-[var(--color-cf-brand)]"
              />
              {t('admin.infraCosts.form.amountIsEstimate')}
            </label>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {field('nextDueDate', t('admin.infraCosts.form.nextDueDate'), (p) => (
                <input
                  {...p}
                  type="date"
                  className={`${INPUT} font-mono`}
                  value={form.nextDueDate}
                  onChange={(e) => set('nextDueDate', e.target.value)}
                />
              ))}
              {field('remindDaysBefore', t('admin.infraCosts.form.remindDaysBefore'), (p) => (
                <input
                  {...p}
                  inputMode="numeric"
                  className={`${INPUT} font-mono`}
                  value={form.remindDaysBefore}
                  onChange={(e) => set('remindDaysBefore', e.target.value)}
                />
              ))}
            </div>
            {field('billingUrl', t('admin.infraCosts.form.billingUrl'), (p) => (
              <input
                {...p}
                type="url"
                className={INPUT}
                value={form.billingUrl}
                onChange={(e) => set('billingUrl', e.target.value)}
              />
            ))}
            {field('notes', t('admin.infraCosts.form.notes'), (p) => (
              <textarea
                {...p}
                rows={3}
                className={`${INPUT} h-auto py-2`}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            ))}
            {failure && (
              <p role="alert" className="rounded-cf-md border border-cf-danger bg-cf-danger-tint px-3 py-2 text-[13px] text-cf-danger">
                {financeErrorMessage(failure) ?? t('admin.infraCosts.form.saveFailed')}
              </p>
            )}
          </div>
          <div className="flex gap-2 border-t border-cf-line bg-cf-surface px-6 pt-3.5 pb-[18px]">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="h-[46px] rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[14px] font-semibold focus-visible:outline-2 focus-visible:outline-cf-ink"
            >
              {t('admin.infraCosts.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="infra-service-submit"
              className="h-[46px] flex-1 rounded-cf-md bg-cf-brand text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:opacity-40"
            >
              {busy ? t('admin.infraCosts.form.saving') : t('admin.infraCosts.form.save')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

interface FieldProps {
  id: string;
  name: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error: string | undefined;
  children: (props: Omit<FieldProps, 'name'>) => ReactNode;
}) {
  const errorId = `${id}-error`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[12px] font-semibold text-cf-ink-muted">
        {label}
      </label>
      {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': error ? errorId : undefined })}
      {error && (
        <p id={errorId} className="text-[12px] text-cf-danger" data-field-error>
          {error}
        </p>
      )}
    </div>
  );
}
